
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const results = [
  {
    id: 1,
    company: 'TechStore France',
    sector: 'E-Commerce',
    sectorIcon: 'ri-store-3-line',
    size: '45 employés · Paris',
    agent: 'Agent IA Vendeur',
    agentColor: '#39FF14',
    avatar: 'https://public.readdy.ai/ai/img_res/79816ce6055c3de8e568c27839185b81.jpg',
    authorName: 'Sophie Martin',
    authorRole: 'Directrice E-commerce',
    quote: 'En 3 mois, notre taux de conversion a triplé. L\'agent répond instantanément aux questions techniques et guide les clients vers l\'achat. Le ROI est tout simplement extraordinaire.',
    verified: true,
    verifiedSource: 'Étude de cas vérifiée · 6 mois de données',
    metrics: [
      { label: 'Taux de conversion', before: '1.2%', after: '4.6%', delta: '+280%', icon: 'ri-arrow-up-circle-line', color: '#39FF14' },
      { label: 'Panier moyen', before: '180€', after: '340€', delta: '+89%', icon: 'ri-shopping-cart-line', color: '#00A8FF' },
      { label: 'Coûts support', before: '12 500€/mois', after: '4 200€/mois', delta: '-65%', icon: 'ri-money-euro-circle-line', color: '#FF6B35' },
    ],
    roi: '22 550%',
    payback: '2 jours',
    slug: 'case-studies',
  },
  {
    id: 2,
    company: 'Clinique Santé Plus',
    sector: 'Santé & Bien-être',
    sectorIcon: 'ri-heart-pulse-line',
    size: '12 praticiens · Lyon',
    agent: 'Agent IA Prise de RDV',
    agentColor: '#FF6B35',
    avatar: 'https://readdy.ai/api/search-image?query=distinguished-male-doctor-professional-portrait-white-coat-confident-warm-smile-modern-clinic-clean-background-medical-professional-natural-lighting-trustworthy-appearance&width=80&height=80&seq=result-avatar-jean-002&orientation=squarish',
    authorName: 'Dr. Jean Dupont',
    authorRole: 'Directeur Médical',
    quote: 'Nous ne perdons plus un seul patient par manque de disponibilité. L\'agent gère les prises de RDV 24/7 et nos no-shows ont chuté de 78%. C\'est une révolution pour notre clinique.',
    verified: true,
    verifiedSource: 'Étude de cas vérifiée · 4 mois de données',
    metrics: [
      { label: 'RDV automatisés', before: '120/sem.', after: '480/sem.', delta: '+300%', icon: 'ri-calendar-check-line', color: '#FF6B35' },
      { label: 'Taux de no-show', before: '18%', after: '4%', delta: '-78%', icon: 'ri-user-unfollow-line', color: '#39FF14' },
      { label: 'Revenus mensuels', before: '85 000€', after: '142 000€', delta: '+67%', icon: 'ri-line-chart-line', color: '#9D4EDD' },
    ],
    roi: '28 442%',
    payback: '1 jour',
    slug: 'case-studies',
  },
  {
    id: 3,
    company: 'SaaS Academy',
    sector: 'Formation SaaS',
    sectorIcon: 'ri-graduation-cap-line',
    size: '28 employés · Bordeaux',
    agent: 'Agent IA Support Technique',
    agentColor: '#4CC9F0',
    avatar: 'https://readdy.ai/api/search-image?query=young-male-tech-entrepreneur-professional-portrait-casual-blazer-confident-smile-modern-startup-office-clean-background-natural-lighting-approachable-founder-appearance&width=80&height=80&seq=result-avatar-julien-003&orientation=squarish',
    authorName: 'Julien Moreau',
    authorRole: 'CEO & Co-fondateur',
    quote: 'Notre support était notre point faible, c\'est devenu notre force. L\'agent résout 87% des demandes instantanément. Le churn a chuté de 71%, soit 95 000€ de revenus conservés chaque mois.',
    verified: true,
    verifiedSource: 'Étude de cas vérifiée · 5 mois de données',
    metrics: [
      { label: 'Tickets résolus auto', before: '0%', after: '87%', delta: '+87pts', icon: 'ri-checkbox-circle-line', color: '#4CC9F0' },
      { label: 'Taux de churn', before: '12%', after: '3.5%', delta: '-71%', icon: 'ri-user-unfollow-line', color: '#39FF14' },
      { label: 'Coûts support', before: '18 000€/mois', after: '6 500€/mois', delta: '-64%', icon: 'ri-money-euro-circle-line', color: '#FF6B35' },
    ],
    roi: '53 366%',
    payback: '< 1 jour',
    slug: 'case-studies',
  },
];

const globalMetrics = [
  { value: '+340%', label: 'Conversions moyennes', icon: 'ri-arrow-up-circle-line', color: '#39FF14' },
  { value: '87%', label: 'Tâches automatisées', icon: 'ri-robot-line', color: '#00A8FF' },
  { value: '-71%', label: 'Réduction du churn', icon: 'ri-user-heart-line', color: '#FF6B35' },
  { value: '8 sec', label: 'Temps de réponse moyen', icon: 'ri-flashlight-line', color: '#9D4EDD' },
  { value: '500+', label: 'PME équipées en France', icon: 'ri-building-2-line', color: '#FF006E' },
  { value: '4.9/5', label: 'Note satisfaction client', icon: 'ri-star-fill', color: '#FFB800' },
];

export default function ClientResults() {
  const [activeCard, setActiveCard] = useState<number>(0);

  return (
    <section className="py-28 bg-gradient-to-b from-black via-[#050a0f] to-black relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-teal-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
            <span className="text-sm text-gray-300 font-medium">Données réelles · Vérifiées · Auditées</span>
          </div>
          <h2 className="text-5xl lg:text-6xl font-display font-bold text-white mb-5 leading-tight">
            Des résultats mesurables,<br />
            <span className="bg-gradient-to-r from-teal-400 to-cyan-300 bg-clip-text text-transparent">pas des promesses</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Chaque chiffre ci-dessous provient d'études de cas réelles, avec données avant/après sur minimum 4 mois d'utilisation.
          </p>
        </motion.div>

        {/* Global KPIs bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 lg:grid-cols-6 gap-4 mb-20"
        >
          {globalMetrics.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 text-center hover:border-white/20 transition-all duration-300"
            >
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                <i className={`${m.icon} text-xl`} style={{ color: m.color }}></i>
              </div>
              <div className="text-2xl font-bold text-white font-display mb-1" style={{ color: m.color }}>{m.value}</div>
              <div className="text-xs text-gray-500 leading-tight">{m.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Case cards selector */}
        <div className="flex flex-wrap gap-3 justify-center mb-10">
          {results.map((r, i) => (
            <button
              key={r.id}
              onClick={() => setActiveCard(i)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeCard === i
                  ? 'bg-white text-black border-white'
                  : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/30 hover:text-white'
              }`}
            >
              <i className={`${r.sectorIcon} text-base`}></i>
              {r.company}
            </button>
          ))}
        </div>

        {/* Active case study */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.35 }}
            className="bg-white/[0.03] border border-white/10 rounded-3xl overflow-hidden"
          >
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Left: Testimonial */}
              <div className="p-10 lg:p-12 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-white/10">
                {/* Company badge */}
                <div>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10">
                      <i className={`${results[activeCard].sectorIcon} text-lg text-white`}></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm">{results[activeCard].company}</div>
                      <div className="text-gray-500 text-xs">{results[activeCard].size}</div>
                    </div>
                    <div className="ml-auto">
                      <span
                        className="text-xs font-semibold px-3 py-1 rounded-full border"
                        style={{ color: results[activeCard].agentColor, borderColor: results[activeCard].agentColor + '40', backgroundColor: results[activeCard].agentColor + '15' }}
                      >
                        {results[activeCard].agent}
                      </span>
                    </div>
                  </div>

                  {/* Quote */}
                  <div className="relative mb-8">
                    <i className="ri-double-quotes-l text-5xl text-white/10 absolute -top-3 -left-2"></i>
                    <blockquote className="text-xl text-white leading-relaxed font-light pl-4 relative z-10">
                      "{results[activeCard].quote}"
                    </blockquote>
                  </div>
                </div>

                {/* Author */}
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <img
                      src={results[activeCard].avatar}
                      alt={results[activeCard].authorName}
                      width="56"
                      height="56"
                      className="w-14 h-14 rounded-full object-cover object-top border-2 border-white/20"
                    />
                    <div>
                      <div className="text-white font-semibold">{results[activeCard].authorName}</div>
                      <div className="text-gray-400 text-sm">{results[activeCard].authorRole}</div>
                      <div className="text-gray-500 text-xs">{results[activeCard].company}</div>
                    </div>
                    {results[activeCard].verified && (
                      <div className="ml-auto flex items-center gap-1.5 bg-green-500/10 border border-green-500/30 rounded-full px-3 py-1">
                        <i className="ri-shield-check-fill text-green-400 text-sm"></i>
                        <span className="text-green-400 text-xs font-medium">Vérifié</span>
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1.5">
                    <i className="ri-information-line"></i>
                    {results[activeCard].verifiedSource}
                  </div>
                </div>
              </div>

              {/* Right: Metrics */}
              <div className="p-10 lg:p-12">
                <div className="text-sm text-gray-500 uppercase tracking-widest mb-6 font-medium">Résultats mesurés · Avant / Après</div>

                <div className="space-y-5 mb-8">
                  {results[activeCard].metrics.map((metric, mi) => (
                    <motion.div
                      key={mi}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: mi * 0.1 }}
                      className="bg-white/[0.04] border border-white/10 rounded-2xl p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: metric.color + '20' }}>
                            <i className={`${metric.icon} text-sm`} style={{ color: metric.color }}></i>
                          </div>
                          <span className="text-gray-300 text-sm font-medium">{metric.label}</span>
                        </div>
                        <span className="text-sm font-bold px-2.5 py-0.5 rounded-full" style={{ color: metric.color, backgroundColor: metric.color + '15' }}>
                          {metric.delta}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 text-center bg-white/5 rounded-xl py-2">
                          <div className="text-xs text-gray-600 mb-0.5">Avant</div>
                          <div className="text-white font-semibold text-sm">{metric.before}</div>
                        </div>
                        <i className="ri-arrow-right-line text-gray-600"></i>
                        <div className="flex-1 text-center rounded-xl py-2" style={{ backgroundColor: metric.color + '15', border: `1px solid ${metric.color}30` }}>
                          <div className="text-xs mb-0.5" style={{ color: metric.color + 'aa' }}>Après</div>
                          <div className="font-bold text-sm" style={{ color: metric.color }}>{metric.after}</div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* ROI summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border border-teal-500/20 rounded-2xl p-4 text-center">
                    <div className="text-xs text-teal-400/70 mb-1 uppercase tracking-wide">ROI total</div>
                    <div className="text-3xl font-bold text-teal-400 font-display">{results[activeCard].roi}</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-4 text-center">
                    <div className="text-xs text-green-400/70 mb-1 uppercase tracking-wide">Retour sur invest.</div>
                    <div className="text-3xl font-bold text-green-400 font-display">{results[activeCard].payback}</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-14"
        >
          <p className="text-gray-500 text-sm mb-5">
            Résultats obtenus par de vraies PME françaises · Données auditées sur 4 à 6 mois
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 bg-white text-black font-semibold px-7 py-3.5 rounded-full hover:bg-gray-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-chart-line"></i>
              Voir toutes les études de cas
            </Link>
            <Link
              to="/demo"
              className="inline-flex items-center gap-2 bg-white/5 border border-white/20 text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line"></i>
              Voir une démo
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
